/**
 * useNotesList Hook
 *
 * Business logic for notes list view including:
 * - Fetching notes with pagination
 * - Search functionality with debouncing
 * - Filter by status (all, reviewing, completed)
 * - Sort order (newest/oldest)
 * - Date range filtering
 * - Note title only search toggle
 * - Batch selection for delete operations
 * - Event tracking
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessToken,useUserState } from '@features/user';
import { useEventTracking } from '@services/statsig';
import type { Dayjs } from 'dayjs';

import { useDebounce } from '../../../hooks/useDebounce';
import { NotesLoader } from '../../../loaders/DashboardLoader';
import APIService from '../../../services/APIService';
import type { UserNoteTypeII } from '../../../types/types';
import {
  clearAllNotesCaches,
  getCachedNotes,
  isCacheValid,
  setCachedNotes,
} from '../../../utils/notesCache';

export type NoteCategory = 'all' | 'reviewing' | 'completed';
export type SortOrder = -1 | 1; // -1 = newest first, 1 = oldest first

export interface UseNotesListOptions {
  /** Initial items per page */
  initialPageSize?: number;
  /** Group ID for group notes list (undefined for user notes) */
  groupId?: string;
  /** Enable date range filtering */
  enableDateFilter?: boolean;
}

export interface UseNotesListReturn {
  // Data
  notes: UserNoteTypeII[];
  isLoading: boolean;
  isLoadingFresh: boolean;
  error: boolean;
  totalCount: number;

  // Pagination
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (size: number) => void;

  // Filters
  category: NoteCategory;
  sortOrder: SortOrder;
  searchTerm: string;
  noteTitleOnly: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  setCategory: (category: NoteCategory) => void;
  setSortOrder: (order: SortOrder) => void;
  setSearchTerm: (term: string) => void;
  setNoteTitleOnly: (titleOnly: boolean) => void;
  setStartDate: (date: Dayjs | null) => void;
  setEndDate: (date: Dayjs | null) => void;

  // Selection
  selectedNoteIds: string[];
  isAllSelected: boolean;
  toggleNoteSelection: (noteId: string) => void;
  selectAllNotes: () => void;
  clearSelection: () => void;

  // Actions
  deleteSelectedNotes: () => Promise<void>;
  navigateToNote: (note: UserNoteTypeII) => void;
  refresh: () => void;

  // State helpers
  isEmpty: boolean;
  isSearchActive: boolean;
  hasFirstNote: boolean;

  // Feature flags
  isEhrMode: boolean;
}

export function useNotesList(options: UseNotesListOptions = {}): UseNotesListReturn {
  const { initialPageSize = 20, groupId, enableDateFilter = true } = options;

  const { getAccessToken } = useAccessToken();
  const userState = useUserState();
  const navigate = useNavigate();
  const { note: noteMetrics } = useEventTracking();

  const isEhrMode = userState?.featureFlags?.ehr_mode ?? false;

  // Core state
  const [notes, setNotes] = useState<UserNoteTypeII[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFresh, setIsLoadingFresh] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPageState] = useState(1);
  const [itemsPerPage, setItemsPerPageState] = useState<number>(() => {
    const saved = localStorage.getItem('noteItemsPerPage');
    return saved ? parseInt(saved) : initialPageSize;
  });

  // Filter state
  const [category, setCategoryState] = useState<NoteCategory>(() => {
    const saved = localStorage.getItem('noteFilterSelection');
    return (saved as NoteCategory) || 'all';
  });

  const [sortOrder, setSortOrderState] = useState<SortOrder>(() => {
    const saved = localStorage.getItem('noteSortOrder');
    return saved ? (parseInt(saved) as SortOrder) : -1;
  });

  const [searchTerm, setSearchTermState] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [noteTitleOnly, setNoteTitleOnlyState] = useState<boolean>(() => {
    const saved = localStorage.getItem('noteTitleOnly');
    return saved ? (JSON.parse(saved) as boolean) : false;
  });

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Selection state
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);

  // Effective group ID (for group notes list)
  const effectiveGroupId = groupId ?? userState?.group_ids?.[0];

  const getCurrentFilters = useCallback(
    () => ({
      category,
      sortOrder,
      searchTerm: debouncedSearchTerm,
      itemsPerPage,
      currentPage,
      startDate,
      endDate,
      noteTitleOnly,
      groupId: effectiveGroupId,
    }),
    [
      category,
      sortOrder,
      debouncedSearchTerm,
      itemsPerPage,
      currentPage,
      startDate,
      endDate,
      noteTitleOnly,
      effectiveGroupId,
    ],
  );

  const fetchAndUpdateNotes = useCallback(
    async (showLoading = true) => {
      // For group notes, ensure we have a group ID
      if (groupId !== undefined && !effectiveGroupId) {
        setIsLoading(false);
        setNotes([]);
        return;
      }

      if (showLoading) {
        setIsLoading(true);
      }
      setIsLoadingFresh(true);

      try {
        const currentFilters = getCurrentFilters();
        const { notes: fetchedNotes, totalCount: fetchedTotalCount } = await NotesLoader(
          currentFilters.category,
          itemsPerPage,
          (currentPage - 1) * itemsPerPage,
          sortOrder,
          currentFilters.searchTerm,
          groupId !== undefined ? effectiveGroupId : undefined,
          enableDateFilter && startDate ? (startDate.valueOf() / 1000).toString() : null,
          enableDateFilter && endDate ? (endDate.valueOf() / 1000).toString() : null,
          currentFilters.noteTitleOnly,
        );

        setNotes(fetchedNotes);
        setTotalCount(fetchedTotalCount);
        setError(false);

        // Update cache
        setCachedNotes({
          notes: fetchedNotes,
          totalCount: fetchedTotalCount,
          filters: currentFilters,
          timestamp: Date.now(),
        });
      } catch (err) {
        setError(true);
        console.error('Error fetching notes:', err);
      } finally {
        setIsLoading(false);
        setIsLoadingFresh(false);
        setIsInitialLoad(false);
      }
    },
    [
      getCurrentFilters,
      itemsPerPage,
      currentPage,
      sortOrder,
      startDate,
      endDate,
      effectiveGroupId,
      groupId,
      enableDateFilter,
    ],
  );

  // Fetch notes on filter changes
  useEffect(() => {
    // For group notes, ensure we have a group ID
    if (groupId !== undefined && !effectiveGroupId) {
      setIsLoading(false);
      setNotes([]);
      return;
    }

    const currentFilters = getCurrentFilters();
    const cache = getCachedNotes(currentFilters);

    if (cache?.notes && isCacheValid(cache, currentFilters)) {
      // Use cached data immediately
      setNotes(cache.notes);
      setTotalCount(cache.totalCount);
      setIsLoading(false);
      setIsInitialLoad(false);

      // Fetch fresh data in background
      void fetchAndUpdateNotes(false);
    } else {
      // No valid cache, do normal fetch
      void fetchAndUpdateNotes(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    itemsPerPage,
    category,
    sortOrder,
    debouncedSearchTerm,
    startDate,
    endDate,
    noteTitleOnly,
    effectiveGroupId,
  ]);

  // Pagination handlers
  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page);
  }, []);

  const setItemsPerPage = useCallback((size: number) => {
    localStorage.setItem('noteItemsPerPage', size.toString());
    setItemsPerPageState(size);
    setCurrentPageState(1);
  }, []);

  // Filter handlers
  const setCategory = useCallback((newCategory: NoteCategory) => {
    localStorage.setItem('noteFilterSelection', newCategory);
    setCategoryState(newCategory);
    setCurrentPageState(1);
    setSelectedNoteIds([]);
  }, []);

  const setSortOrder = useCallback(
    (newOrder: SortOrder) => {
      localStorage.setItem('noteSortOrder', newOrder.toString());

      // Check cache before showing loading state
      const newFilters = {
        ...getCurrentFilters(),
        sortOrder: newOrder,
      };

      const cache = getCachedNotes(newFilters);
      if (cache?.notes && isCacheValid(cache, newFilters)) {
        setNotes(cache.notes);
        setTotalCount(cache.totalCount);
        setIsLoadingFresh(true);
        setIsLoading(false);
      } else {
        setNotes([]);
        setIsLoading(true);
      }

      setSortOrderState(newOrder);
    },
    [getCurrentFilters],
  );

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  const setNoteTitleOnly = useCallback((titleOnly: boolean) => {
    localStorage.setItem('noteTitleOnly', JSON.stringify(titleOnly));
    setNoteTitleOnlyState(titleOnly);
  }, []);

  // Selection handlers
  const toggleNoteSelection = useCallback((noteId: string) => {
    setSelectedNoteIds(prev =>
      prev.includes(noteId) ? prev.filter(id => id !== noteId) : [...prev, noteId],
    );
  }, []);

  const selectAllNotes = useCallback(() => {
    setSelectedNoteIds(prev =>
      prev.length === notes.length ? [] : notes.map(note => note.note_id),
    );
  }, [notes]);

  const clearSelection = useCallback(() => {
    setSelectedNoteIds([]);
  }, []);

  // Actions
  const deleteSelectedNotes = useCallback(async () => {
    if (selectedNoteIds.length === 0) return;

    const accessToken = await getAccessToken();
    const deletePromises = selectedNoteIds.map(noteId =>
      APIService.makeAPIPostRequest({
        requestString: '/notes/delete',
        accessToken,
        body: { note_id: noteId },
      }),
    );

    await Promise.all(deletePromises);

    // Clear cache and refresh
    clearAllNotesCaches();
    setSelectedNoteIds([]);
    await fetchAndUpdateNotes(true);
  }, [selectedNoteIds, getAccessToken, fetchAndUpdateNotes]);

  const navigateToNote = useCallback(
    (note: UserNoteTypeII) => {
      if (note.status === 'errored') return;

      noteMetrics?.viewed?.(note.note_id, { source: 'list' });

      // Check for thread_id first (V2 flow) - if present, navigate to note page regardless of status
      const threadId = (note as { thread_id?: string | null })?.thread_id;
      const path = threadId
        ? `/note/${note.note_id}`
        : note.status === 'completed'
          ? `/note/${note.note_id}`
          : `/status/${note.note_id}`;
      navigate(path, { state: { origin: groupId ? 'group' : 'user' } });
    },
    [navigate, noteMetrics, groupId],
  );

  const refresh = useCallback(() => {
    void fetchAndUpdateNotes(true);
  }, [fetchAndUpdateNotes]);

  // Computed values
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / itemsPerPage)),
    [totalCount, itemsPerPage],
  );

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const isEmpty = !isLoading && notes.length === 0;
  const isSearchActive = debouncedSearchTerm.length > 0;
  const isAllSelected = notes.length > 0 && selectedNoteIds.length === notes.length;
  const hasFirstNote = userState?.onboardingSteps?.generated_first_note ?? false;

  return {
    // Data
    notes,
    isLoading,
    isLoadingFresh: isLoadingFresh && !isInitialLoad,
    error,
    totalCount,

    // Pagination
    currentPage,
    itemsPerPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    setCurrentPage,
    setItemsPerPage,

    // Filters
    category,
    sortOrder,
    searchTerm,
    noteTitleOnly,
    startDate,
    endDate,
    setCategory,
    setSortOrder,
    setSearchTerm,
    setNoteTitleOnly,
    setStartDate,
    setEndDate,

    // Selection
    selectedNoteIds,
    isAllSelected,
    toggleNoteSelection,
    selectAllNotes,
    clearSelection,

    // Actions
    deleteSelectedNotes,
    navigateToNote,
    refresh,

    // State helpers
    isEmpty,
    isSearchActive,
    hasFirstNote,

    // Feature flags
    isEhrMode,
  };
}
